import { AnalyticsStore } from 'bernie-plugin-mobx';
import { inject, observer } from 'mobx-react';
import { Lambda, reaction } from 'mobx';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as React from 'react';

import { Context } from 'bernie-context';
import { Layer } from 'bernie-view';
import { QueryRoute, updateSearch } from 'bernie-core';

import { LocalizedComponentProps, withLocalization } from '@shared-ui/localization-context';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';
import { UIPageStore } from 'stores/ui-page-store';

import { DayName, UitkDatePickerProps, UitkNewDatePicker } from 'uitk-react-date-picker';

import { onClickScrollToTarget } from 'utils/smooth-scroll';

export interface NewDateFieldProps extends Partial<RouteComponentProps> {
  analytics?: AnalyticsStore;
  context?: Context;
  dateFieldProps: object;
  midnightBookingOffsetHours?: number;
  numDaysBookableInAdvance?: number;
  updateModels: (startDate: string, EndDate: string) => void;
  registerFocusMethod?: (fieldName: string, focusMethod: () => void) => void;
  rfrr: object;
  currentHotel?: CurrentHotelStore;
  uiPage?: UIPageStore;
}

export type LocalizedNewDateFieldProps = LocalizedComponentProps & UitkDatePickerProps & NewDateFieldProps;

export interface NewDateFieldState {
  mounted: boolean;
  footerText?: string;
  showDatePicker: boolean;
}

// @ts-ignore
@withRouter
// @ts-ignore
@withLocalization
@inject('analytics', 'context', 'currentHotel', 'uiPage')
@observer
export class NewDateField extends React.Component<LocalizedNewDateFieldProps, NewDateFieldState> {
  public state: Readonly<NewDateFieldState> = {
    mounted: false,
    footerText: undefined,
    showDatePicker: false,
  };

  constructor(props: Readonly<LocalizedNewDateFieldProps>) {
    super(props);

    this.focusButton = React.createRef();

    this.focusDateFieldInput = this.focusDateFieldInput.bind(this);
    this.onMount = this.onMount.bind(this);
    this.dismiss = this.dismiss.bind(this);
    this.open = this.open.bind(this);
    this.save = this.save.bind(this);
  }

  public focusButton: any;
  public startRef: { current: any } | undefined;
  public endRef: { current: any } | undefined;
  private datePickerAnalytics = {};
  private disposerUiPageOpenDatePickerEvent: Lambda | undefined;

  public static defaultProps = {
    endDate: '',
    startDate: '',
    l10n: null,
  };

  public componentDidMount() {
    const { registerFocusMethod, uiPage } = this.props;
    registerFocusMethod?.('endDate', this.focusDateFieldInput);
    this.setState({ mounted: true });
    this.disposerUiPageOpenDatePickerEvent = reaction(
      () => uiPage?.openNewDatePickerEvent,
      openNewDatePickerEvent => {
        if (openNewDatePickerEvent) {
          this.setState({ showDatePicker: true });
        }
      }
    );
  }

  public componentWillUnmount() {
    if (this.disposerUiPageOpenDatePickerEvent) {
      this.disposerUiPageOpenDatePickerEvent();
    }
  }

  public open(dateType: any, isMobile: boolean) {
    const { history, location } = this.props;

    if (isMobile && history && location) {
      updateSearch({ history, location, newParams: { pwaDialog: `datepicker-${dateType}` } });
    } else {
      onClickScrollToTarget({ scrollTo: 0 });
    }

    this.setState({ showDatePicker: true });
  }

  public save(start: any, end: any, isMobile: boolean) {
    const { endDate, l10n, startDate, updateModels } = this.props;
    const { formatDate } = l10n;
    const formattedEnd = formatDate(end, { raw: 'yyyy-MM-dd' });
    const formattedStart = formatDate(start, { raw: 'yyyy-MM-dd' });

    // prevents submitting the datepicker when no new dates are selected
    if (endDate !== formattedEnd || startDate !== formattedStart) {
      updateModels(formattedStart, formattedEnd);
    }

    this.dismiss(isMobile, false);
  }

  public focusDateFieldInput(): void {
    const { dateFieldProps } = this.props;
    const { endInvalid, startInvalid } = dateFieldProps;

    if (startInvalid) {
      this.focusButton = this.startRef && this.startRef.current;
    }

    if (endInvalid) {
      this.focusButton = this.endRef && this.endRef.current;
    }

    // manage focus back to the trigger
    if (this.focusButton && typeof this.focusButton.focus === 'function') {
      this.focusButton.focus();
    }
  }

  public dismiss(isMobile: boolean, noUpdate: boolean) {
    if (isMobile) {
      this.props.history?.goBack();
    }

    if (!noUpdate) {
      this.setState({ showDatePicker: false });
    }
  }

  public onMount(start: any, end: any) {
    this.startRef = start;
    this.endRef = end;
  }

  public render() {
    const {
      analytics,
      buttonText,
      context,
      dateFieldProps,
      endDate,
      midnightBookingOffsetHours,
      numDaysBookableInAdvance,
      startDate,
      l10n,
    } = this.props;

    const { mounted, showDatePicker } = this.state;
    const firstDayOfWeek = parseInt(DayName[l10n.getWeekData().firstDay.toUpperCase() as DayName], 10);

    this.datePickerAnalytics = {
      closeAnalytics: analytics.setupTracker(':ChangeDatesClose', 'Close Date Picker', null),
      doneAnalytics: analytics.setupTracker(':ChangeDatesDone', 'Apply Date Picker', null),
      nextPageAnalytics: analytics.setupTracker(':NextMonthClick', 'Next Month Click', null),
      previousPageAnalytics: analytics.setupTracker(':PreviousMonthClick', 'Previous Month Click', null),
    };

    let enhancedDateFieldProps = {
      ...dateFieldProps,
      showDatePicker,
      endAnalytics: analytics.setupTracker(':ChangeDatesCheckOut', 'Open Date Picker'),
      startAnalytics: analytics.setupTracker(':ChangeDatesCheckIn', 'Open Date Picker'),
    };

    if (!mounted) {
      enhancedDateFieldProps = {
        ...enhancedDateFieldProps,
        endPlaceholder: context?.localDateFormat,
        startPlaceholder: context?.localDateFormat,
      };
    }

    const dialogWrapper = (children: React.ReactNode, dateType: any) => (
      <QueryRoute query={{ pwaDialog: `datepicker-${dateType}` }}>
        <Layer id={`home-datepicker-${dateType}`} onDismiss={this.dismiss}>
          {children}
        </Layer>
      </QueryRoute>
    );

    const datePickerProps = {
      analytics: this.datePickerAnalytics,
      buttonText,
      dateFieldProps: enhancedDateFieldProps,
      daysBookableInAdvance: numDaysBookableInAdvance,
      dialogWrapper,
      doubleMonths: true,
      endDate,
      footerText: this.state.footerText,
      midnightBookingOffset: midnightBookingOffsetHours,
      onDismissCB: this.dismiss,
      onMountCB: this.onMount,
      onOpenCB: this.open,
      onSubmitCB: this.save,
      startDate,
      firstDayOfWeek,
    };

    return <UitkNewDatePicker {...datePickerProps} />;
  }
}
